import type { TFunction } from 'i18next'
import type { Conversation } from '@/types/conversation'

export type TitledConversation = Pick<Conversation, 'topic' | 'outcome' | 'updated_at'>

/** Past this long without an update, an unclassified conversation is treated as stuck, not ongoing */
export const ONGOING_MAX_AGE_MS = 3 * 60 * 60 * 1000

/**
 * The backend sets `topic` and `outcome` together once a conversation has been
 * processed. Without either, the conversation has not been classified yet, so it is
 * still running (or about to be processed) rather than untitled. If it has not been
 * updated for a few hours, processing most likely failed, so it is no longer ongoing.
 */
export function isOngoingConversation(
  conversation: TitledConversation,
  now: number = Date.now()
): boolean {
  if (conversation.topic?.trim() || conversation.outcome) return false

  const updatedAt = Date.parse(conversation.updated_at)
  return Number.isNaN(updatedAt) || now - updatedAt <= ONGOING_MAX_AGE_MS
}

export function getInboxTitle(
  conversation: TitledConversation,
  t: TFunction,
  now: number = Date.now()
): string {
  const topic = conversation.topic?.trim()
  if (topic) return topic
  return isOngoingConversation(conversation, now)
    ? t('resources.conversations.inbox.ongoing')
    : t('resources.conversations.inbox.untitled')
}
